import { apiEmployeeGoals } from "@/api/employees/employee-goal.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { goalsKeys } from "@/queries/goals.query.ts";
import { ApiErrorException } from "@/types/api.types.ts";
import { ApiGoalPayload, ApiGoalSearchFilter } from "@/types/goals.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

export const employeeGoalsKeys = createQueryKeys("employeeGoals", {
  search: (employeeId: number, filter: ApiGoalSearchFilter) => ({
    queryKey: [employeeId, filter],
    queryFn: async () => {
      const result = await apiEmployeeGoals.search(employeeId, filter);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const useEmployeeGoalsSearch = (
  employeeId: number,
  filter: ApiGoalSearchFilter,
  enabled: boolean = true
) => {
  return useQuery({
    ...employeeGoalsKeys.search(employeeId, filter),
    enabled: enabled && !!employeeId,
  });
};

interface EmployeeGoalCreateParams {
  employeeId: number;
  payload: ApiGoalPayload;
}

export const useEmployeeGoalCreate = <T extends FieldValues>(
  control: Control<T> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ employeeId, payload }: EmployeeGoalCreateParams) => {
      const result = await apiEmployeeGoals.create(employeeId, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: goalsKeys.search._def });
      queryClient.invalidateQueries({
        queryKey: employeeGoalsKeys.search._def,
        predicate: (query) => query.queryKey[2] === variables.employeeId,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};
